import { FormEvent } from 'react';

import { Button } from '../../components/Button';
import { FormField } from '../../components/FormField';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { CloseIcon } from '../../icons/CloseIcon';
import {
  CloseButton,
  FormBlock,
  FormError,
  ForgotRow,
  Heading,
  HeadingAccent,
  HeadingBlock,
  InlineLink,
  Panel,
  SignUpRow,
} from './SignInModal.styles';
import type { SignInModalLabels, SignInModalProps } from './SignInModal.types';

const DEFAULT_LABELS: Required<Omit<SignInModalLabels, never>> = {
  title: 'Sign in.',
  subtitle: 'Good things come to those who log in.',
  signUpPrompt: "Don't have an account?",
  signUpLink: 'Sign up',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  submit: 'Sign in',
  forgottenPassword: 'Forgotten password?',
  close: 'Close',
};

export function SignInModal({
  open,
  onClose,
  values,
  onChange,
  onSubmit,
  onSignUpClick,
  onForgottenPasswordClick,
  isSubmitting = false,
  errors,
  labels,
}: SignInModalProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    onSubmit();
  };

  return (
    <Modal open={open} onClose={onClose} ariaLabel={merged.title}>
      <Panel data-testid="sign-in-modal">
        <CloseButton type="button" onClick={onClose} aria-label={merged.close}>
          <CloseIcon />
        </CloseButton>

        <HeadingBlock>
          <Heading>
            {merged.title} <HeadingAccent>{merged.subtitle}</HeadingAccent>
          </Heading>
          <SignUpRow>
            {merged.signUpPrompt}{' '}
            <InlineLink type="button" onClick={onSignUpClick}>
              {merged.signUpLink}
            </InlineLink>
          </SignUpRow>
        </HeadingBlock>

        <FormBlock onSubmit={handleSubmit} noValidate>
          <FormField label={merged.emailLabel} required error={errors?.email}>
            <Input
              type="email"
              autoComplete="email"
              value={values.email}
              onChange={(e) => onChange('email', e.target.value)}
              error={Boolean(errors?.email)}
              aria-label={merged.emailLabel}
            />
          </FormField>

          <FormField label={merged.passwordLabel} required error={errors?.password}>
            <Input
              type="password"
              autoComplete="current-password"
              value={values.password}
              onChange={(e) => onChange('password', e.target.value)}
              error={Boolean(errors?.password)}
              aria-label={merged.passwordLabel}
            />
          </FormField>

          {errors?.form && <FormError role="alert">{errors.form}</FormError>}

          <Button type="submit" variant="primary" fullWidth disabled={isSubmitting}>
            {merged.submit}
          </Button>
        </FormBlock>

        <ForgotRow>
          <InlineLink type="button" onClick={onForgottenPasswordClick}>
            {merged.forgottenPassword}
          </InlineLink>
        </ForgotRow>
      </Panel>
    </Modal>
  );
}
